import { formatPackPrice } from '@/shared/lib/format-catalog-price';
import { annualOfferSku, passCheckoutSku } from '@/features/payment/lib/pass-sku';
import type {
  CheckoutAlternative,
  CheckoutDescriptor,
  ProPlanAnnualOffer,
} from '@/types/checkout';
import type { Pass } from '@/types/passes';

export type ProPlanPeriod = 'month' | 'year';

/**
 * The monthly Pro Plan card, normalized from either wire shape (GET /passes
 * resource or GET /store item) so one view-model serves every surface.
 */
export interface ProPlanOfferSource {
  price: number;
  currency: string;
  localized_price?: string | null;
  checkout?: CheckoutDescriptor | null;
  checkout_alternative?: CheckoutAlternative | null;
  sku: string;
  periodLabel?: string | null;
  billingNote?: string | null;
  /** Price-free verb CTA for the monthly side (the price shows separately). */
  ctaLabel: string;
  annualOffer?: ProPlanAnnualOffer | null;
  isOwned: boolean;
}

/** What a Pro Plan card shows for the selected period. */
export interface ProPlanOfferView {
  period: ProPlanPeriod;
  /** Display price. Monthly carries the client "/month" suffix (when passed);
   *  yearly is the bare price, its period comes from the API copy. */
  priceLabel: string;
  billingNote: string | null;
  ctaLabel: string;
  checkout: CheckoutDescriptor | null;
  checkoutAlternative: CheckoutAlternative | null;
  sku: string;
}

export interface ProPlanPeriodOption {
  id: ProPlanPeriod;
  label: string;
}

export function proPlanSourceFromPass(pass: Pass): ProPlanOfferSource {
  const { attributes } = pass;
  return {
    price: attributes.price,
    currency: attributes.currency,
    localized_price: attributes.localized_price,
    checkout: attributes.checkout,
    checkout_alternative: attributes.checkout_alternative,
    sku: passCheckoutSku(pass),
    periodLabel: attributes.paywall.period_label,
    billingNote: attributes.paywall.billing_note,
    ctaLabel: attributes.paywall.cta_label_plain ?? attributes.paywall.cta_label,
    annualOffer: attributes.annual_offer,
    isOwned: attributes.is_owned === true || attributes.is_active === true,
  };
}

/**
 * Switch segments, or null when the switch must not render: no annual offer
 * on sale, the plan is already owned, or the payload predates the switch (no
 * monthly `period_label`). The yearly segment carries the server savings
 * badge as `Yearly · Save 36%` (same composition on Apple and Android).
 */
export function proPlanPeriodOptions(
  source: ProPlanOfferSource
): ProPlanPeriodOption[] | null {
  const annual = source.annualOffer;
  if (!annual || source.isOwned || !source.periodLabel) return null;
  const savings = annual.copy.savings_label;
  return [
    { id: 'month', label: source.periodLabel },
    {
      id: 'year',
      label: savings
        ? `${annual.copy.period_label} · ${savings}`
        : annual.copy.period_label,
    },
  ];
}

/**
 * Resolve the card fields for a period. Yearly without an annual offer (it
 * went off sale while selected) resolves to monthly, so a stale selection can
 * never point checkout at a missing price.
 */
export function resolveProPlanOffer(
  source: ProPlanOfferSource,
  period: ProPlanPeriod,
  monthlySuffix = ''
): ProPlanOfferView {
  const annual = source.annualOffer;
  if (period === 'year' && annual && !source.isOwned) {
    return {
      period: 'year',
      priceLabel: formatPackPrice(annual),
      billingNote: annual.copy.billing_note,
      ctaLabel: annual.copy.cta_label,
      checkout: annual.checkout,
      checkoutAlternative: annual.checkout_alternative,
      sku: annualOfferSku(annual),
    };
  }
  return {
    period: 'month',
    priceLabel: `${formatPackPrice(source)}${monthlySuffix}`,
    billingNote: source.billingNote ?? null,
    ctaLabel: source.ctaLabel,
    checkout: source.checkout ?? null,
    checkoutAlternative: source.checkout_alternative ?? null,
    sku: source.sku,
  };
}
